import React, { useEffect, useRef, useState } from "react";
import VoiceInput from "../../../../shared/VoiceInput/VoiceInput";
import { AppMarkdown } from "../../../../shared/AppMarkdown/AppMarkdown";
import { MessageLoadingAnimation } from "../../../../../base/LoadingAnimation";
import "../OpcComposer/_opc-composer.scss";
import "./_opc-chat-view.scss";

// ═══════════════════════════════════════════════════════════════════════════
// OKR Plans (coach) chat view — the SME chat look (CD33): assistant icon,
// bubbles, "Thinking…" row, streaming cursor, full-height skeleton, and the
// SME-style composer with suggestions inside the input. Replaces today's
// `ProjectChat` inside OKR Plans only (Xray keeps `ProjectChat`).
//   renderCustomMessage(message, index) draws senders this view doesn't know
//   (remark / summary-change cards); quickStarters = suggestion chips.
// ═══════════════════════════════════════════════════════════════════════════

const ASSISTANT_ICON = "/new-theme-assets/small-logo.png";

export const OpcAssistantIcon = ({ thinking = false }) => (
  <div className="cp-assistant-icon d-flex align-items-center justify-content-center flex-shrink-0">
    <img
      src={ASSISTANT_ICON}
      alt="assistant"
      width={24}
      height={24}
      className={thinking ? "cp-assistant-icon-loading" : undefined}
    />
  </div>
);

const ChatSkeleton = ({ withInput }) => (
  <div className="opc-chat-skel" aria-busy="true" aria-hidden="true">
    <div className="cp-skeleton">
      {[0, 1].map((cycle) => (
        <React.Fragment key={cycle}>
          <div className="cp-skeleton-row cp-skeleton-row--assistant">
            <div className="cp-skeleton-avatar cp-skeleton-pulse" />
            <div className="cp-skeleton-bubble cp-skeleton-pulse" />
          </div>
          <div className="cp-skeleton-row cp-skeleton-row--user">
            <div className="cp-skeleton-bubble cp-skeleton-pulse" />
          </div>
        </React.Fragment>
      ))}
    </div>
    {withInput ? (
      <div className="cp-input-area-skeleton cp-skeleton-pulse" />
    ) : null}
  </div>
);

// CD50: the one chat skeleton — `OpcChatView` while history loads, and the
// coach split pages (check-in, review) while they load, so the page never
// swaps one skeleton for another.
export const OpcChatSkeleton = ({ withInput = true }) => (
  <div className="opc-chat-view">
    <ChatSkeleton withInput={withInput} />
  </div>
);

const ReasoningRow = ({ message }) => {
  const [open, setOpen] = useState(false);
  const secs = message.durationSec || 0;
  return (
    <div className="cp-reasoning-row">
      <button
        type="button"
        className="d-flex align-items-center gap-2 border-0 bg-transparent p-0 light-text small"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <i className="fa-regular fa-lightbulb" aria-hidden="true" />
        Thought for {secs} second{secs === 1 ? "" : "s"}
        <i
          className={`fa-solid fa-chevron-down cp-reasoning-chevron${open ? " cp-reasoning-chevron--open" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open ? (
        <div className="explanation-main p-3 d-flex flex-column">
          <AppMarkdown>{message.message}</AppMarkdown>
        </div>
      ) : null}
    </div>
  );
};

const OpcChatView = ({
  messages = [],
  loading,
  streamLoading,
  streamMessage,
  messageLoading,
  readOnly,
  error,
  onSend,
  renderCustomMessage,
  footerNote,
  quickStarters = [],
}) => {
  const listRef = useRef(null);
  const busy = !!(loading || streamLoading);

  // Follow the conversation: stick to the bottom as messages / tokens arrive.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, streamMessage, loading, messageLoading]);

  if (messageLoading)
    return (
      <div className="opc-chat-view">
        <ChatSkeleton withInput={!readOnly} />
      </div>
    );

  return (
    <div className="opc-chat-view">
      <div ref={listRef} className="cp-messages flex-grow-1 d-flex flex-column">
        {messages.map((message, index) => {
          if (message.sender === "user") {
            return (
              <div
                key={message.message_id || `u-${index}`}
                className="cp-message-row cp-message-row--user d-flex align-items-start w-100"
              >
                <div className="cp-bubble-wrap cp-bubble-wrap--user">
                  <div className="cp-bubble cp-bubble--user">
                    <span className="white-space-pre-wrap">
                      {message.message}
                    </span>
                  </div>
                </div>
              </div>
            );
          }
          if (message.sender === "assistant") {
            return (
              <div
                key={message.message_id || `a-${index}`}
                className="cp-message-row cp-message-row--assistant d-flex align-items-start w-100"
              >
                <OpcAssistantIcon />
                <div className="cp-bubble-wrap">
                  <div className="cp-bubble cp-bubble--assistant">
                    <AppMarkdown>{message.message}</AppMarkdown>
                  </div>
                </div>
              </div>
            );
          }
          if (message.sender === "reason_summary") {
            return (
              <ReasoningRow
                key={message.message_id || `r-${index}`}
                message={message}
              />
            );
          }
          return renderCustomMessage
            ? renderCustomMessage(message, index)
            : null;
        })}

        {loading && !streamLoading ? (
          <div className="cp-loading-row d-flex align-items-start">
            <OpcAssistantIcon thinking />
            <div
              className="cp-thinking-bubble d-flex align-items-center mt-1"
              aria-label="Assistant is thinking"
            >
              <span className="cp-thinking-label me-2">Thinking</span>
              <MessageLoadingAnimation />
            </div>
          </div>
        ) : null}

        {streamLoading ? (
          <div className="cp-message-row cp-message-row--assistant d-flex align-items-start w-100">
            <OpcAssistantIcon />
            <div className="cp-bubble-wrap">
              <div className="cp-bubble cp-bubble--assistant cp-bubble--streaming">
                <AppMarkdown>{streamMessage || ""}</AppMarkdown>
                <span className="cp-cursor" aria-hidden="true" />
              </div>
            </div>
          </div>
        ) : null}
        <div className="cp-scroll-anchor" />
      </div>

      {error ? <div className="opc-chat-error">{error}</div> : null}
      {footerNote ? <div className="opc-chat-note">{footerNote}</div> : null}

      {!readOnly ? (
        <div className="cp-input-area flex-shrink-0 opc-composer">
          <VoiceInput
            value=""
            placeholder="Reply here..."
            onSend={(text) => {
              if (text?.trim()) onSend(text.trim());
            }}
            onPlus={() => {}}
            fileMultiple={false}
            showMic
            disabled={busy}
            files={[]}
            quickStarters={busy ? [] : quickStarters}
          />
        </div>
      ) : null}
    </div>
  );
};

export default OpcChatView;
